"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

const STORAGE_KEY = "spicenet-masquerade-wallet";

interface MasqueradeContextType {
  /** The wallet the admin is currently impersonating, or null. */
  impersonatedWallet: string | null;
  isMasquerading: boolean;
  startMasquerade: (wallet: string) => void;
  stopMasquerade: () => void;
}

const MasqueradeContext = createContext<MasqueradeContextType | undefined>(
  undefined,
);

export const MasqueradeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Initialize to null so server and first client render agree (no hydration
  // mismatch), then hydrate from sessionStorage on the client. Persistence
  // happens in the setters — impersonation survives navigation/reload within
  // the tab and is cleared when the tab closes.
  const [impersonatedWallet, setImpersonatedWallet] = useState<string | null>(
    null,
  );

  useEffect(() => {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    if (stored) setImpersonatedWallet(stored);
  }, []);

  const startMasquerade = useCallback((wallet: string) => {
    const normalized = wallet.trim().toLowerCase();
    window.sessionStorage.setItem(STORAGE_KEY, normalized);
    setImpersonatedWallet(normalized);
  }, []);

  const stopMasquerade = useCallback(() => {
    window.sessionStorage.removeItem(STORAGE_KEY);
    setImpersonatedWallet(null);
  }, []);

  const value: MasqueradeContextType = {
    impersonatedWallet,
    isMasquerading: Boolean(impersonatedWallet),
    startMasquerade,
    stopMasquerade,
  };

  return (
    <MasqueradeContext.Provider value={value}>
      {children}
    </MasqueradeContext.Provider>
  );
};

export const useMasquerade = () => {
  const context = useContext(MasqueradeContext);
  if (context === undefined) {
    throw new Error("useMasquerade must be used within a MasqueradeProvider");
  }
  return context;
};
